import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'

import CardLists from '@/components/CardLists/CardLists'
import DefaultHead from '@/components/DefaultHead/DefaultHead'
import Main from '@/components/Main/Main'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import Description from '@/components/Panels/BottomPanel/Description/Description'
import SimpleHeader from '@/components/SimpleHeader/SimpleHeader'
import SubMenu from '@/components/SubMenu/SubMenu'
import Tabs from '@/components/Tabs/Tabs'
import { GenericPageProps } from '@/types/page'
import { bracketsArticleUrl } from '@/utils/constants'
import { serverSidePropsFor } from '@/utils/helpers.server'
import useDirectories from '@/utils/useDirectories'

const SUBMENU_ITEMS = [
  { name: 'Past 2 Years', url: '' },
  { name: 'Past Month', url: '/month' },
  { name: 'Past Week', url: '/week' },
]

const SALT_MENU_ITEMS = [
  { name: '2024', url: '/top/salt' },
  { name: '2023', url: '/top/salt/2023' },
  { name: '2022', url: '/top/salt/2022' },
  { name: '2021', url: '/top/salt/2021' },
  { name: '2020', url: '/top/salt/2020' },
  { name: '2019', url: '/top/salt/2019' },
]

const TIMESPAN_SLUGS = [undefined, 'month', 'week']

const COLOR_SLUGS = ['white', 'blue', 'black', 'red', 'green', 'colorless', 'multicolor']

const TopPage = ({ data }: GenericPageProps) => {
  const { subpage, subsubpage } = useDirectories()
  const router = useRouter()

  const { slug } = router.query
  const isColorPage = COLOR_SLUGS.includes(slug as string)
  const isSaltPage = `${slug}`.startsWith('salt')
  const isTypePage = !isColorPage && !isSaltPage && !TIMESPAN_SLUGS.includes(subpage)
  const isTimespanPage = !isColorPage && !isSaltPage && subpage !== 'game-changers'
  const topLeftEdge = isSaltPage || isTypePage ? !subsubpage : !subpage

  const description =
    slug === 'game-changers' ? (
      <>
        <p>
          <b>Game changers</b> are part of the new bracket system being tested by Wizard of the Coast.
        </p>
        <p>
          These cards dramatically warp commander games, whether having players run away with resources, shift in ways
          many players dislike, cause people to not be able to play the game, really efficiently search for their
          strongest cards, or commanders that tend to be only built in ways that take away from more casual games.
        </p>
        <ul>
          <li>
            Bracket 1 <i>(exhibition)</i> and 2 <i>(core)</i> decks generally avoid game changers.
          </li>
          <li>
            Bracket 3 <i>(upgraded)</i> decks generally run up to 3 game changers.
          </li>
          <li>
            Bracket 4 <i>(optimized)</i> and 5 <i>(cEDH)</i> are unrestricted on game changers.
          </li>
        </ul>
        <p className='mb-0'>
          <OutboundLink url={bracketsArticleUrl}>Click here to learn more about Brackets.</OutboundLink>
        </p>
      </>
    ) : (
      data.description
    )

  return (
    <Main>
      <DefaultHead data={data} />
      <SimpleHeader>{data.header}</SimpleHeader>
      <Description>{description}</Description>
      <Tabs>
        {isSaltPage && <SubMenu items={SALT_MENU_ITEMS} />}
        {isTimespanPage && (
          <SubMenu
            items={SUBMENU_ITEMS.map((item) => ({
              ...item,
              url: TIMESPAN_SLUGS.includes(slug as string) ? `/top${item.url}` : `/top/${slug}${item.url}`,
            }))}
          />
        )}
      </Tabs>
      <CardLists
        disabledSortMethods={isSaltPage ? ['percent', 'synergy'] : ['synergy']}
        initCardLists={data.container.json_dict.cardlists}
        topLeftEdge={topLeftEdge}
      />
    </Main>
  )
}

export const getServerSideProps: GetServerSideProps<GenericPageProps> = async ({ req }) =>
  serverSidePropsFor(`${process.env.NEXT_PUBLIC_EDHREC_JSON_URL}/pages/top/year.json`, req.headers)

export default TopPage
